import React, { useState } from 'react'

type Page = 'landing' | 'dashboard' | 'worklog' | 'feedback'

interface HeaderProps {
  currentPage: Page
  onNavigate: (page: Page) => void
  onLogout?: () => void
  username?: string
  isDarkMode?: boolean
  onToggleDarkMode?: () => void
  isAuthenticated?: boolean
  onLogoClick?: () => void
}

export const Header: React.FC<HeaderProps> = ({
  currentPage,
  onNavigate,
  onLogout,
  username,
  isDarkMode = false,
  onToggleDarkMode,
  isAuthenticated = true,
  onLogoClick
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // 로그인 전에는 항상 dark, 로그인 후에는 toggle 가능
  const actualDarkMode = !isAuthenticated || isDarkMode

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick()
    } else if (isAuthenticated) {
      onNavigate('landing')
    }
  }

  return (
    <header className={`shadow-lg fixed top-0 left-0 right-0 z-50 transition-colors ${actualDarkMode
      ? 'bg-black'
      : 'bg-gradient-to-r from-primary-800 to-primary-700'
      }`}>
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* 로고 및 타이틀 */}
          <div className="flex items-center space-x-3 md:space-x-6">
            <div className="flex flex-col">
              <h1 className="text-xl md:text-4xl font-black text-white tracking-wider font-orbitron leading-tight cursor-pointer" onClick={handleLogoClick}>
                WIZ OCM
              </h1>
              <span className={`text-xs md:text-sm font-medium tracking-wide mt-1 ${actualDarkMode ? 'text-gray-400' : 'text-primary-200'
                }`}>
                Wizbase Oracle Cloud Manager
              </span>
            </div>
          </div>

          {/* 데스크톱 네비게이션 - 로그인 후에만 표시 */}
          {isAuthenticated && (
            <nav className="hidden lg:flex items-center space-x-6">
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); onNavigate('dashboard'); }}
                className={`transition-colors font-semibold text-lg ${currentPage === 'dashboard'
                  ? 'text-white'
                  : actualDarkMode
                    ? 'text-gray-400 hover:text-white'
                    : 'text-primary-200 hover:text-white'
                  }`}
              >
                비용분석
              </a>
              <a href="#" className={`transition-colors font-semibold text-lg ${actualDarkMode ? 'text-gray-400 hover:text-white' : 'text-primary-200 hover:text-white'
                }`}>
                보안
              </a>
              <a href="#" className={`transition-colors font-semibold text-lg ${actualDarkMode ? 'text-gray-400 hover:text-white' : 'text-primary-200 hover:text-white'
                }`}>
                재해복구
              </a>
              <a href="#" className={`transition-colors font-semibold text-lg ${actualDarkMode ? 'text-gray-400 hover:text-white' : 'text-primary-200 hover:text-white'
                }`}>
                리소스분석
              </a>
              <a href="#" className={`transition-colors font-semibold text-lg ${actualDarkMode ? 'text-gray-400 hover:text-white' : 'text-primary-200 hover:text-white'
                }`}>
                알람
              </a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); onNavigate('worklog'); }}
                className={`transition-colors font-semibold text-lg ${currentPage === 'worklog'
                  ? 'text-white'
                  : actualDarkMode
                    ? 'text-gray-400 hover:text-white'
                    : 'text-primary-200 hover:text-white'
                  }`}
              >
                지원이력
              </a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); onNavigate('feedback'); }}
                className={`transition-colors font-semibold text-lg ${currentPage === 'feedback'
                  ? 'text-white'
                  : actualDarkMode
                    ? 'text-gray-400 hover:text-white'
                    : 'text-primary-200 hover:text-white'
                  }`}
              >
                피드백
              </a>
            </nav>
          )}

          {/* 데스크톱 액션 버튼 */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated && (
              <a
                href="https://partnerstore.oracle.com/apex/f?p=500:2325:15528333856967:::RP,2325:LOCALE_READ_ONLY,RESET_MENU,TOP_MENU_ID,CHILD_MENU_ID,FUNCTION_ID:N,N,1105009178400737772342,53778214201075213407,&cs=3bSvaNPUGi4tHVniHxTk-napi5FacfJMuzLQfbix1cSs1T-yCX2dKL8feG0KBwBlDU0oYBflVQ5Fei1ej0UOUlg#NULL#"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center px-4 py-2 rounded-md font-medium transition-colors ${actualDarkMode
                  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  : 'bg-white text-primary-700 hover:bg-gray-100'
                  }`}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#FF6600">
                  <rect x="2" y="6" width="20" height="12" rx="6" fill="#FF6600" />
                  <rect x="4" y="8" width="16" height="8" rx="4" fill="white" />
                </svg>
                OPS
              </a>
            )}
            {isAuthenticated && onToggleDarkMode && (
              <button
                onClick={onToggleDarkMode}
                className="relative w-16 h-8 rounded-full transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                style={{
                  backgroundColor: isDarkMode ? '#374151' : '#FCD34D'
                }}
                title={isDarkMode ? 'Light Mode로 전환' : 'Dark Mode로 전환'}
              >
                {/* 토글 슬라이더 */}
                <div
                  className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white transition-all duration-300 ease-in-out transform ${isDarkMode ? 'translate-x-8' : 'translate-x-0'
                    } shadow-md flex items-center justify-center`}
                >
                  {isDarkMode ? (
                    <svg className="w-4 h-4 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </button>
            )}
            {username && (
              <span className={`font-medium ${actualDarkMode ? 'text-gray-400' : 'text-primary-200'
                }`}>
                {username}
              </span>
            )}
            {onLogout && (
              <button
                onClick={onLogout}
                className={`
                  group relative px-5 py-2.5 rounded-lg font-semibold transition-all duration-300 ease-in-out
                  ${actualDarkMode
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 hover:shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20 hover:shadow-lg backdrop-blur-sm'
                  }
                  border ${actualDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-white/20 hover:border-white/30'}
                  active:scale-95
                `}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </span>
              </button>
            )}
          </div>

          {/* 모바일 메뉴 버튼 - 로그인 후에만 표시 */}
          {isAuthenticated && (
            <button
              className={`md:hidden p-2 transition-colors ${actualDarkMode ? 'text-gray-400 hover:text-white' : 'text-primary-200 hover:text-white'
                }`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
        </div>

        {/* 모바일 메뉴 */}
        {isMenuOpen && (
          <div className={`md:hidden mt-4 pb-4 border-t ${actualDarkMode ? 'border-gray-700' : 'border-primary-700'
            }`}>
            <nav className="flex flex-col space-y-3 pt-4">
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); onNavigate('dashboard'); setIsMenuOpen(false); }}
                className={`transition-colors font-semibold text-base ${currentPage === 'dashboard'
                  ? 'text-white'
                  : actualDarkMode
                    ? 'text-gray-400 hover:text-white'
                    : 'text-primary-200 hover:text-white'
                  }`}
              >
                비용분석
              </a>
              <a href="#" className={`transition-colors font-semibold text-base ${actualDarkMode ? 'text-gray-400 hover:text-white' : 'text-primary-200 hover:text-white'
                }`}>
                보안
              </a>
              <a href="#" className={`transition-colors font-semibold text-base ${actualDarkMode ? 'text-gray-400 hover:text-white' : 'text-primary-200 hover:text-white'
                }`}>
                재해복구
              </a>
              <a href="#" className={`transition-colors font-semibold text-base ${actualDarkMode ? 'text-gray-400 hover:text-white' : 'text-primary-200 hover:text-white'
                }`}>
                리소스분석
              </a>
              <a href="#" className={`transition-colors font-semibold text-base ${actualDarkMode ? 'text-gray-400 hover:text-white' : 'text-primary-200 hover:text-white'
                }`}>
                알람
              </a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); onNavigate('worklog'); setIsMenuOpen(false); }}
                className={`transition-colors font-semibold text-base ${currentPage === 'worklog'
                  ? 'text-white'
                  : actualDarkMode
                    ? 'text-gray-400 hover:text-white'
                    : 'text-primary-200 hover:text-white'
                  }`}
              >
                지원이력
              </a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); onNavigate('feedback'); setIsMenuOpen(false); }}
                className={`transition-colors font-semibold text-base ${currentPage === 'feedback'
                  ? 'text-white'
                  : actualDarkMode
                    ? 'text-gray-400 hover:text-white'
                    : 'text-primary-200 hover:text-white'
                  }`}
              >
                피드백
              </a>
              {isAuthenticated && (
                <a
                  href="https://partnerstore.oracle.com/apex/f?p=500:2325:15528333856967:::RP,2325:LOCALE_READ_ONLY,RESET_MENU,TOP_MENU_ID,CHILD_MENU_ID,FUNCTION_ID:N,N,1105009178400737772342,53778214201075213407,&cs=3bSvaNPUGi4tHVniHxTk-napi5FacfJMuzLQfbix1cSs1T-yCX2dKL8feG0KBwBlDU0oYBflVQ5Fei1ej0UOUlg#NULL#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center px-4 py-2 rounded-md font-medium transition-colors ${actualDarkMode
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-white text-primary-700 hover:bg-gray-100'
                    }`}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#FF6600">
                    <rect x="2" y="6" width="20" height="12" rx="6" fill="#FF6600" />
                    <rect x="4" y="8" width="16" height="8" rx="4" fill="white" />
                  </svg>
                  OPS
                </a>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}
